/**
 * 报表文件 CRUD。对应后端 FileServletHandler。
 *
 * 关键契约（源码已确认）：
 * - create：POST body {parentId, name, code, desc, type:"report"}，parentId=0 为根目录；
 *   返回含 id 的 BaskFile。
 * - update：POST body {xml, base64Data?}（xml 必须 URL 编码），文件 id 经 ?f=<fileId> 传递。
 *   服务端执行 URLDecoder.decode(xml,"UTF-8")，故提交前必须对 xml 做 encodeURIComponent。
 * - 存在 bask.file.updateable 开关与文件 checkout 锁；需识别这两类错误并明确提示。
 */

import {
  Session,
  authedRequest,
  parseJson,
  ApiError,
} from './session.js';
import { fileAction, packetAction } from './urls.js';

export interface CreateReportOptions {
  parentId?: number; // 缺省 0（根）
  name: string;
  desc?: string;
}

export interface BaskFile {
  id: number;
  name: string;
  code?: string;
  [key: string]: any;
}

/** 创建报表文件，返回文件记录（含 id）。同名冲突时自动追加序号重试。 */
export async function createReport(
  session: Session,
  opts: CreateReportOptions,
): Promise<BaskFile> {
  const url = fileAction(session.baseUrl, 'create');
  let lastErr: ApiError | undefined;
  for (let i = 0; i <= 9; i++) {
    const name = i === 0 ? opts.name : `${opts.name} (${i})`;
    const resp = await authedRequest(session, url, {
      method: 'POST',
      headers: { 'content-type': 'application/json; charset=utf-8' },
      body: JSON.stringify({
        parentId: opts.parentId ?? 0,
        name,
        code: '',
        desc: opts.desc ?? '',
        type: 'report',
      }),
    });
    if (resp.statusCode === 200) {
      const json = await parseJson(resp);
      return json.data ?? json;
    }
    const text = await resp.body.text();
    // 同名冲突：换名重试；其他错误直接抛出
    if (!/重复|duplicate|exist/i.test(text)) {
      throw new ApiError(`创建报表失败 (${resp.statusCode})：${text.slice(0, 300)}`, resp.statusCode, text);
    }
    lastErr = new ApiError(`创建报表失败 (${resp.statusCode})：${text.slice(0, 300)}`, resp.statusCode, text);
  }
  throw lastErr!;
}

/**
 * 写入报表 XML。参考浏览器实际契约：
 *   POST /api/file/update?file=<fileId>&online=false&tenantId=<租户>
 *   Content-Type: application/json
 *   Body: { "base64Data": "", "xml": "<encodeURIComponent(xml)>" }
 * 与服务端 URLDecoder.decode 对应，否则 +/%/& 会被错误解码导致报表损坏。
 */
/**
 * 发布（deploy）报表文件，把 update 写入内容后的草稿转正为正式资源文件。
 *   POST /api/file/deploy?file=<fileId>&online=false&tenantId=<租户>
 * 必须先 update（写入 XML 生成资源文件）再 deploy，否则 deploy 会报"资源文件【id】不存在"。
 */
export async function deployReport(
  session: Session,
  fileId: number,
): Promise<void> {
  const url = `${fileAction(session.baseUrl, 'deploy')}?file=${fileId}&online=false&tenantId=${session.tenantId}`;
  const resp = await authedRequest(session, url, { method: 'POST' });
  if (resp.statusCode !== 200) {
    const text = await resp.body.text();
    throw new ApiError(`发布报表失败 (${resp.statusCode})：${text.slice(0, 200)}`, resp.statusCode, text);
  }
}

/**
 * 修改报表前先“发布”当前版本：调用后端 /api/file/deploy，让后台把当前线上内容
 * 封存为一个可回滚的版本快照。随后再做 update 覆盖新内容，即可在后期出问题时恢复。
 *
 * 复用 deploy 端点（deploy 即发布并生成版本）；区别在于它发生在 update 之前，
 * 目的是“先快照再变更”，而非“草稿转正”。若当前文件不存在资源文件（如刚创建尚未
 * update 过），deploy 会失败，此时视为无版本可保留，返回 false 由调用方跳过。
 *
 * @param session 会话（含 baseUrl、tenantId、鉴权）
 * @param fileId 报表文件 id
 * @returns 是否已发布（false 表示无可快照版本，已安全跳过）
 */
export async function publishReport(
  session: Session,
  fileId: number,
): Promise<boolean> {
  const url = `${fileAction(session.baseUrl, 'deploy')}?file=${fileId}&online=false&tenantId=${session.tenantId}`;
  const resp = await authedRequest(session, url, { method: 'POST' });
  if (resp.statusCode === 200) return true;
  const text = await resp.body.text();
  // 资源文件尚未就绪（从未 update 过）：无可快照版本，跳过 publish 即可。
  if (/资源文件【\d+】不存在/.test(text)) return false;
  throw new ApiError(`发布当前版本失败 (${resp.statusCode})：${text.slice(0, 200)}`, resp.statusCode, text);
}

export async function updateReportXml(
  session: Session,
  fileId: number,
  xml: string,
): Promise<void> {
  const url = `${fileAction(session.baseUrl, 'update')}?file=${fileId}&online=false&tenantId=${session.tenantId}`;
  const encoded = encodeURIComponent(xml);
  // 服务端在 create 后资源文件就绪存在短暂延迟：首次 update 偶发 500「资源文件【id】不存在」，
  // 重试即可成功。最多重试 5 次，退避 500ms*尝试次数。
  let lastErr: ApiError | undefined;
  for (let attempt = 0; attempt < 5; attempt++) {
    if (attempt > 0) await new Promise((r) => setTimeout(r, 500 * attempt));
    const resp = await authedRequest(session, url, {
      method: 'POST',
      headers: { 'content-type': 'application/json; charset=utf-8' },
      body: JSON.stringify({ base64Data: '', xml: encoded }),
    });
    if (resp.statusCode === 200) return;
    const text = await resp.body.text();
    // 资源尚未就绪的瞬时错误：重试
    if (/资源文件【\d+】不存在/.test(text)) {
      lastErr = new ApiError(`写入报表失败 (${resp.statusCode})：${text.slice(0, 200)}`, resp.statusCode, text);
      continue;
    }
    // 识别常见错误：文件被锁、禁止保存
    if (/checkout|locked|被锁定|占用/i.test(text)) {
      throw new ApiError(
        `文件 ${fileId} 被 checkout 锁定，请先在服务端解锁或稍后重试。`,
        resp.statusCode,
        text,
      );
    }
    if (/updateable|禁止保存|演示环境|readonly/i.test(text)) {
      throw new ApiError(
        `服务端禁止保存（bask.file.updateable=false 或演示环境），请检查配置。`,
        resp.statusCode,
        text,
      );
    }
    throw new ApiError(`写入报表失败 (${resp.statusCode})：${text.slice(0, 200)}`, resp.statusCode, text);
  }
  throw lastErr ?? new ApiError(`写入报表失败：重试后仍无法更新文件 ${fileId}`);
}

/**
 * 列出租户下某目录的子节点（目录 + 报表文件）。
 * 端点：{baseUrl}/baskserver/api/packet/loadChildren?parentId=<id>&recursive=<bool>
 * 对应后端 PacketServletHandler.loadChildren：
 *   - parentId 缺省为根目录（BaskFile.ROOT_FILE_ID）；
 *   - recursive=true 时一次返回含嵌套 children 的完整子树（目录节点带 children 字段）；
 *   - recursive=false（缺省）时仅返回下一层，目录节点不含 children。
 * 返回的子节点：报表文件含 { id, name, code?, type:"report" }；目录节点含
 *   { id, name, directory:true, file:false } 且无 type 字段。recursive=true 的目录节点带嵌套 children。
 *
 * 注意：服务端需部署 recursive 透传（见 PacketServletHandler.loadChildren 五参版 loadChildren），
 * 旧版未部署时 recursive 被忽略、始终只返一层，listAllReportFiles 会自动退化为客户端下钻。
 *
 * @param session 会话
 * @param parentId 父目录 ID，0 表示根目录
 * @param opts.recursive 是否递归返回完整子树，缺省 false
 */
export async function listReportFiles(
  session: Session,
  parentId: number,
  opts: { recursive?: boolean } = {},
): Promise<BaskFile[]> {
  const recursive = opts.recursive ? 'true' : 'false';
  const url = `${packetAction(session.baseUrl, 'loadChildren')}?parentId=${parentId}&recursive=${recursive}`;
  const resp = await authedRequest(session, url, { method: 'GET' });
  if (resp.statusCode !== 200) {
    const text = await resp.body.text();
    throw new ApiError(`列举报表失败 (${resp.statusCode})：${text.slice(0, 200)}`, resp.statusCode, text);
  }
  const json = await parseJson(resp);
  const data = json?.data ?? json;
  // 兼容 { data: [...] } / 顶层数组 / { children: [...] } 多种返回形态
  const arr = Array.isArray(data) ? data : data?.children ?? [];
  return Array.isArray(arr) ? arr : [];
}

/**
 * 递归遍历目录树，收集全部符合 type 的文件（缺省 type==="report"）。
 * 从 parentId 出发，优先利用服务端 recursive=true 一次拿完整子树（深度遍历收集）；
 * 若服务端未部署 recursive 透传（目录节点无嵌套 children），则自动退化为逐层并发下钻，
 * 行为与旧版一致。用于 report list 不指定具体目录时一次拉全租户内所有报表。
 *
 * 优化点：
 * - 服务端递归优先：部署 recursive 透传后单 HTTP 请求即可拿全量，零多次 RTT。
 * - 并发下钻兜底：未部署时同层目录并行拉取（受 maxConcurrency 限制），层次深时提速。
 * - limit 保护：一旦收集到的报表数达到 limit，立即停止递归（不再下钻剩余目录），
 *   防止报表/目录过多时一次性把全量元数据灌进上下文，撑爆 LLM token。
 *
 * @param session     会话
 * @param opts.parentId 起始父目录 ID，缺省 0（根目录）
 * @param opts.type      过滤类型，缺省 report
 * @param opts.limit     最大收集条数，达到即停止递归；缺省不限制
 * @param opts.maxConcurrency 同层目录并发数，缺省 6
 * @returns 收集到的文件数组，可能少于实际总数（当触发 limit 时）
 */
export async function listAllReportFiles(
  session: Session,
  opts: { parentId?: number; type?: string; limit?: number; maxConcurrency?: number } = {},
): Promise<BaskFile[]> {
  const targetType = opts.type ?? 'report';
  const limit = opts.limit != null ? opts.limit : Infinity;
  const maxConcurrency = opts.maxConcurrency ?? 6;
  const collected: BaskFile[] = [];

  const walk = async (parentId: number): Promise<void> => {
    if (collected.length >= limit) return;
    const children = await listReportFiles(session, parentId, { recursive: true });
    // 服务端已递归：目录节点带嵌套 children，整棵树已在一次响应内，深度遍历收集即可。
    if (children.some((c: any) => c.directory === true && Array.isArray(c.children))) {
      const stack: BaskFile[] = [...children];
      while (stack.length && collected.length < limit) {
        const node = stack.pop()!;
        if (node.type === targetType) {
          collected.push(node);
        } else if (node.directory === true && Array.isArray(node.children) && node.children.length) {
          stack.push(...node.children);
        }
      }
      return;
    }
    // 服务端未递归（目录无 children）：退化为逐层并发下钻，行为与旧版一致。
    const subDirs: BaskFile[] = [];
    for (const child of children) {
      if (collected.length >= limit) break;
      if (child.type === targetType) {
        collected.push(child);
      } else if (child.directory === true) {
        subDirs.push(child);
      }
    }
    // 并发下钻：把子目录按 maxConcurrency 分批，同批内并行，批间串行。
    for (let i = 0; i < subDirs.length; i += maxConcurrency) {
      if (collected.length >= limit) break;
      const batch = subDirs.slice(i, i + maxConcurrency);
      await Promise.all(batch.map((d) => walk(d.id)));
    }
  };

  await walk(opts.parentId ?? 0);
  // 若触发了 limit，截断到精确条数（同层并发可能略微超出 1 个批次，这里收紧）。
  return collected.length > limit ? collected.slice(0, limit) : collected;
}

/** 读取报表 XML 源。服务端 loadFileSource 参数名为 file（与 update/deploy 一致），勿用 f。 */
export async function loadReportXml(session: Session, fileId: number): Promise<string> {
  const url = `${fileAction(session.baseUrl, 'loadFileSource')}?file=${fileId}`;
  const resp = await authedRequest(session, url, { method: 'GET' });
  if (resp.statusCode !== 200) {
    throw new ApiError(`读取报表失败 (${resp.statusCode})`, resp.statusCode);
  }
  const json = await parseJson(resp);
  return json.data ?? json;
}

/** 重命名。 */
export async function renameReport(
  session: Session,
  fileId: number,
  name: string,
): Promise<void> {
  const url = fileAction(session.baseUrl, 'changeName');
  const resp = await authedRequest(session, url, {
    method: 'POST',
    headers: { 'content-type': 'application/json; charset=utf-8' },
    body: JSON.stringify({ id: fileId, name }),
  });
  if (resp.statusCode !== 200) throw new ApiError(`重命名失败 (${resp.statusCode})`, resp.statusCode);
  await parseJson(resp);
}

/** 删除报表。接口参数名与 update/deploy 一致为 file（非 f），否则服务端报「无法从URL中获取文件信息」。 */
export async function removeReport(session: Session, fileId: number): Promise<void> {
  const url = `${fileAction(session.baseUrl, 'remove')}?file=${fileId}`;
  const resp = await authedRequest(session, url, { method: 'POST' });
  if (resp.statusCode !== 200) throw new ApiError(`删除失败 (${resp.statusCode})`, resp.statusCode);
  await parseJson(resp);
}
